import React from 'react';
import type { ActivityViewProps } from '../../core/types/activity.types';
import type { ArenaMultipleChoiceQuestion } from '../../core/types/payloads/arena.types';
import { LiveChoiceBars } from '../shared/LiveChoiceBars';

const ARENA_SHAPES = ['◆', '●', '▲', '■'];
const ARENA_COLORS = ['#fbbf24', '#f97316', '#ef4444', '#eab308'];

function ArenaShell({ children, badge }: { children: React.ReactNode; badge?: string }) {
  return (
    <div className="arena-shell relative overflow-hidden rounded-2xl p-6 sm:p-8">
      {badge && (
        <span className="arena-shell__badge mb-4 inline-block rounded-full px-3 py-1 text-[10px] uppercase tracking-widest">
          {badge}
        </span>
      )}
      {children}
    </div>
  );
}

function getArenaCounts(
  question: ArenaMultipleChoiceQuestion,
  runtime: ActivityViewProps<'arena'>['runtime'],
) {
  const key = String(runtime.currentQuestionIndex);
  const bucket = (runtime.aggregates[key] as number[] | undefined) ?? question.options.map(() => 0);
  const counts = question.options.map((_, i) => bucket[i] ?? 0);
  const total = counts.reduce((a, b) => a + b, 0);
  return { counts, total };
}

function FeedbackBanner({ correct }: { correct: boolean }) {
  return (
    <div
      className={`mb-6 rounded-2xl border-[1.5px] px-6 py-5 text-center shadow-[3px_3px_0_0_#020617] ${
        correct ? 'border-emerald-800 bg-emerald-100' : 'border-red-800 bg-red-100'
      }`}
    >
      <p className="text-4xl">{correct ? '🎉' : '💪'}</p>
      <p className={`mt-2 font-display text-2xl font-black ${correct ? 'text-emerald-800' : 'text-red-800'}`}>
        {correct ? '¡Correcto!' : 'Incorrecto'}
      </p>
      <p className="mt-1 text-sm font-bold text-slate-600">
        {correct ? 'Buen trabajo' : 'La respuesta correcta está marcada abajo'}
      </p>
    </div>
  );
}

function ArenaPresentation({
  payload,
  runtime,
  question,
  size = 'md',
  badge,
  revealCorrect,
}: {
  payload: ActivityViewProps<'arena'>['payload'];
  runtime: ActivityViewProps<'arena'>['runtime'];
  question: ArenaMultipleChoiceQuestion;
  size?: 'md' | 'lg';
  badge?: string;
  revealCorrect: boolean;
}) {
  const { counts, total } = getArenaCounts(question, runtime);
  const titleSize = size === 'lg' ? 'text-3xl sm:text-5xl md:text-6xl' : 'text-2xl sm:text-4xl';
  const promptSize = size === 'lg' ? 'text-xl sm:text-3xl' : 'text-lg sm:text-2xl';

  return (
    <ArenaShell badge={badge}>
      <h3 className={`arena-prompt-display font-display font-black uppercase tracking-tight ${titleSize}`}>
        {payload.title}
      </h3>
      <p className={`mt-6 font-bold leading-snug text-slate-700 ${promptSize}`}>{question.prompt}</p>

      <div className="mt-8">
        <LiveChoiceBars
          size={size}
          total={total}
          revealCorrect={revealCorrect}
          items={question.options.map((opt, i) => ({
            label: opt,
            count: counts[i] ?? 0,
            accent: ARENA_COLORS[i % ARENA_COLORS.length],
            icon: ARENA_SHAPES[i],
            isCorrect: revealCorrect && question.correctIndex === i,
          }))}
        />
      </div>
    </ArenaShell>
  );
}

export function ArenaHostView(props: ActivityViewProps<'arena'>) {
  const question = props.payload.questions[props.runtime.currentQuestionIndex];
  if (!question || question.kind !== 'multiple-choice') {
    return (
      <ArenaShell>
        <p style={{ color: 'var(--plugin-muted)' }}>Pregunta no soportada</p>
      </ArenaShell>
    );
  }

  const revealed = props.runtime.revealed || props.phase === 'RESULTS';
  const badge = revealed
    ? `Revelado · ${props.runtime.currentQuestionIndex + 1}/${props.payload.questions.length}`
    : `En juego · ${props.runtime.currentQuestionIndex + 1}/${props.payload.questions.length}`;

  return (
    <ArenaPresentation
      payload={props.payload}
      runtime={props.runtime}
      question={question}
      badge={badge}
      revealCorrect={revealed}
    />
  );
}

export function ArenaStudentView(props: ActivityViewProps<'arena'>) {
  const { payload, runtime, dispatch, participantId, phase, examAutonomous } = props;
  const question = payload.questions[runtime.currentQuestionIndex];

  if (!question || question.kind !== 'multiple-choice') {
    return (
      <ArenaShell>
        <p className="font-bold text-slate-600">Pregunta no soportada en demo</p>
      </ArenaShell>
    );
  }

  const myAnswer = participantId ? runtime.answers[participantId]?.[question.id] : undefined;
  const hasAnswered = typeof myAnswer === 'number';
  const waiting = phase === 'PREPARING' || phase === 'LOBBY';
  const showCorrectAfter = payload.settings?.showCorrectAfter !== false;
  const instantFeedback = Boolean(examAutonomous && showCorrectAfter);
  const showFeedback =
    runtime.revealed || phase === 'RESULTS' || phase === 'PODIUM' || (instantFeedback && hasAnswered);
  const correctIndex = question.correctIndex;
  const isCorrect = hasAnswered && typeof correctIndex === 'number' && myAnswer === correctIndex;
  const isLastQuestion = runtime.currentQuestionIndex >= payload.questions.length - 1;
  const hasMoreQuestions = hasAnswered && !isLastQuestion;

  const nextQuestion = () => {
    void dispatch({ type: 'NEXT_QUESTION', payload: {} });
  };

  if (waiting) {
    return (
      <ArenaShell badge="Arena">
        <p className="font-display text-xl font-black sm:text-2xl">{question.prompt}</p>
        <p className="mt-6 rounded-xl border-[1.5px] border-slate-950 bg-white px-4 py-6 text-center text-lg font-bold text-slate-600 shadow-[3px_3px_0_0_#1e293b]">
          ¡Prepárate! El profesor va a comenzar…
        </p>
      </ArenaShell>
    );
  }

  if (showFeedback) {
    return (
      <ArenaShell badge="Resultado">
        {hasAnswered && typeof correctIndex === 'number' && <FeedbackBanner correct={isCorrect} />}
        {!hasAnswered && (
          <p className="mb-4 text-center text-sm" style={{ color: 'var(--plugin-muted)' }}>
            No respondiste a tiempo
          </p>
        )}
        <p className="font-display text-xl font-bold">{question.prompt}</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {question.options.map((option, index) => {
            const isMine = myAnswer === index;
            const isRight = correctIndex === index;
            return (
              <div
                key={option}
                className={`rounded-2xl border-[1.5px] px-5 py-5 font-display text-base font-black sm:text-lg shadow-[3px_3px_0_0_#1e293b] ${
                  isRight
                    ? 'border-emerald-800 bg-emerald-100 text-emerald-900'
                    : isMine
                      ? 'border-red-800 bg-red-100 text-red-900'
                      : 'border-slate-950 bg-white text-slate-950'
                }`}
              >
                <span className="mb-2 block text-2xl">{ARENA_SHAPES[index]}</span>
                {option}
                {isRight && <span className="mt-2 block text-xs font-black uppercase text-emerald-800">Correcta</span>}
                {isMine && !isRight && <span className="mt-2 block text-xs font-black uppercase text-red-700">Tu respuesta</span>}
              </div>
            );
          })}
        </div>
        {examAutonomous && hasMoreQuestions && (
          <button type="button" className="sala-btn-hero mt-6 w-full text-xs" onClick={nextQuestion}>
            Siguiente pregunta →
          </button>
        )}
      </ArenaShell>
    );
  }

  if (hasAnswered && examAutonomous && !showCorrectAfter) {
    return (
      <ArenaShell badge="Respuesta registrada">
        <p className="font-display text-xl font-black sm:text-2xl">{question.prompt}</p>
        <div className="mt-6 rounded-2xl border-[1.5px] border-slate-950 bg-white px-5 py-6 text-center shadow-[4px_4px_0_0_#020617]">
          <span className="text-3xl">{ARENA_SHAPES[myAnswer as number]}</span>
          <p className="mt-2 font-display text-xl font-black">{question.options[myAnswer as number]}</p>
        </div>
        {hasMoreQuestions ? (
          <button type="button" className="sala-btn-hero mt-4 w-full text-xs" onClick={nextQuestion}>
            Siguiente pregunta →
          </button>
        ) : (
          <p className="plugin-muted-note mt-4">Respuesta guardada · avanzando…</p>
        )}
      </ArenaShell>
    );
  }

  if (hasAnswered) {
    return (
      <ArenaShell badge="Respuesta enviada">
        <p className="font-display text-xl font-black sm:text-2xl">{question.prompt}</p>
        <div
          className="mt-6 rounded-2xl border-[1.5px] border-slate-950 bg-white px-5 py-6 text-center shadow-[4px_4px_0_0_#020617]"
        >
          <span className="text-3xl">{ARENA_SHAPES[myAnswer as number]}</span>
          <p className="mt-2 font-display text-xl font-black">{question.options[myAnswer as number]}</p>
        </div>
        <p className="mt-4 text-center text-sm font-bold text-slate-600">Esperando revelación del profesor…</p>
      </ArenaShell>
    );
  }

  return (
    <ArenaShell badge="Arena · Compite">
      <p className="font-display text-xl font-black leading-snug sm:text-2xl">{question.prompt}</p>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {question.options.map((option, index) => (
          <button
            key={option}
            type="button"
            className="arena-option-tile relative overflow-hidden rounded-2xl px-5 py-6 text-left font-display text-base sm:text-lg"
            onClick={() =>
              dispatch({
                type: 'ANSWER',
                payload: { questionId: question.id, value: index },
              })
            }
          >
            <span className="mb-2 block text-2xl">{ARENA_SHAPES[index]}</span>
            {option}
          </button>
        ))}
      </div>
    </ArenaShell>
  );
}

export function ArenaProjectorView(props: ActivityViewProps<'arena'>) {
  const question = props.payload.questions[props.runtime.currentQuestionIndex];
  if (!question || question.kind !== 'multiple-choice') return null;

  const revealed = props.runtime.revealed || props.phase === 'RESULTS';

  return (
    <ArenaPresentation
      payload={props.payload}
      runtime={props.runtime}
      question={question}
      size="lg"
      revealCorrect={revealed}
      badge={
        revealed
          ? `Respuesta revelada · ${props.runtime.currentQuestionIndex + 1}/${props.payload.questions.length}`
          : `En vivo · ${props.runtime.currentQuestionIndex + 1}/${props.payload.questions.length}`
      }
    />
  );
}
